import { useState, useRef } from 'react';
import { subscriptionApi } from '../../api/subscriptionApi';
import SettingsWrapper from './SettingsWrapper';
import toast from 'react-hot-toast';

type ShowKey = 'current' | 'new' | 'confirm';

const EyeIcon = ({ visible }: { visible: boolean }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        {visible ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
    </svg>
);

const SecurityPage = () => {
    const newPassRef = useRef<HTMLInputElement>(null);
    const confirmRef = useRef<HTMLInputElement>(null);
    const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [show, setShow] = useState<Record<ShowKey, boolean>>({ current: false, new: false, confirm: false });

    const toggleShow = (k: ShowKey) => setShow(s => ({ ...s, [k]: !s[k] }));

    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.currentPassword) e.currentPassword = 'Required';
        if (!form.newPassword) e.newPassword = 'Required';
        else if (form.newPassword.length < 6) e.newPassword = 'Minimum 6 characters';
        if (form.newPassword !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            const res = await subscriptionApi.changePassword(form.currentPassword, form.newPassword);
            if (res.success) { toast.success('Password changed!'); setForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); }
            else toast.error(res.message || 'Failed');
        } catch { toast.error('Something went wrong'); }
        finally { setLoading(false); }
    };

    const renderField = (label: string, field: keyof typeof form, showKey: ShowKey, placeholder: string, ref?: React.RefObject<HTMLInputElement | null>, onEnter?: () => void) => (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
            <div className="relative">
                <input ref={ref} type={show[showKey] ? 'text' : 'password'} value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && onEnter?.()}
                    placeholder={placeholder}
                    className={`w-full pl-4 pr-10 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 ${errors[field] ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50 focus:bg-white'}`}/>
                <button type="button" onClick={() => toggleShow(showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <EyeIcon visible={show[showKey]}/>
                </button>
            </div>
            {errors[field] && <p className="text-red-500 text-xs mt-1">{errors[field]}</p>}
        </div>
    );

    return (
        <SettingsWrapper title="Security" subtitle="Change your account password">
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 space-y-5">
                {renderField('Current Password', 'currentPassword', 'current', 'Enter current password', undefined, () => newPassRef.current?.focus())}
                {renderField('New Password', 'newPassword', 'new', 'Minimum 6 characters', newPassRef, () => confirmRef.current?.focus())}
                {renderField('Confirm New Password', 'confirmPassword', 'confirm', 'Re-enter new password', confirmRef, handleSubmit)}
                <div className="flex justify-end pt-1">
                    <button onClick={handleSubmit} disabled={loading}
                        className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all disabled:opacity-60 flex items-center gap-2">
                        {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                        Update Password
                    </button>
                </div>
            </div>
        </SettingsWrapper>
    );
};

export default SecurityPage;